import Immutable from 'immutable';
import { useState } from 'react';
import { Button } from 'edrolo-style-guide';
import {
  EXAM_QUESTION_TYPE_FIELD_INPUT_TYPE_MAPPINGS,
  EXAM_QUESTION_TYPES,
} from 'edrolo/constants/QuestionTypes';
import WistiaUtils from 'edrolo/utils/WistiaUtils';
import { DrawingInputField } from 'core/components';
import {
  uploadUserDrawingAsset,
  fileToBase64,
  AssetUploadContext,
} from 'core/utils';
import IllustrationAnswer from './IllustrationAnswer';

import GrokSelfReflection from '../../../GrokSelfReflection';
import QuestionSolution from '../../../QuestionSolution';
import style from './IllustrationQuestion.scss';
import questionTypePropInterface from '../../questionTypePropInterface';
import { SelfMarking } from '../SelfMarking';
import { WaffleFlag } from 'edrolo/utils/Waffle';

export const IllustrationQuestion = ({
  question,
  response,
  onSubmit,
  onSubmitGrok,
  onSubmitMark,
  onSubmitTeacherAnswer,
  grokkingItemIsSaving,
  isQuestionReview,
  isPrint,
}) => {
  const [fieldResponse, setFieldResponse] = useState(Immutable.Map());

  if (isPrint) {
    return <img src={question.get('image_url')} alt="illustration " />;
  }

  const field = question
    .get('fields')
    .find(
      (field) =>
        field.get('input_type') ===
        EXAM_QUESTION_TYPE_FIELD_INPUT_TYPE_MAPPINGS[
          question.get('question_type')
        ]
    );

  // TODO don't know if this is necessary
  if (!field) {
    return <span>Invalid question setup</span>;
  }

  // Logic to determine if the submission button is enabled
  const existingDocument = fieldResponse?.get('submitted_drawing_json');
  const documentShapes = existingDocument
    ?.get('pages')
    .toList()
    .first()
    .get('shapes')
    .toList();
  const hasEmptyResponse =
    !documentShapes ||
    documentShapes.size === 0 ||
    (documentShapes?.size === 1 &&
      documentShapes?.first().get('id') === 'background-image');

  // If this is a teacher responding, we don't want submitted images to be
  // uploaded anywhere; base-64 encode them as data uris.
  // If it's a student responding, upload the images to s3 via the server.
  const handleAssetCreate = onSubmitTeacherAnswer
    ? fileToBase64
    : uploadUserDrawingAsset;

  const handleSubmitResponse = (json, svg) => {
    const fieldId = question.getIn(['fields', 0, 'id']);
    const fieldResponse = {
      fieldId: fieldId,
      submitted_drawing_json: json,
      submitted_drawing_svg: svg,
    };
    onSubmit({ fieldResponses: [fieldResponse] });
  };

  if (!response) {
    return (
      <AssetUploadContext.Provider value={handleAssetCreate}>
        <div
          className={style.inputField}
          data-test={`question-${EXAM_QUESTION_TYPES.ILLUSTRATION}`}
        >
          <DrawingInputField
            key={field.get('id')}
            tldrawDocument={fieldResponse?.get('submitted_drawing_json')}
            backgroundImageURL={question.get('image_url')}
            onPersist={(document_json, document_svg) => {
              setFieldResponse(
                Immutable.fromJS({
                  fieldId: field.get('id'),
                  submitted_drawing_json: document_json,
                  submitted_drawing_svg: document_svg,
                })
              );
            }}
          />
        </div>
        <Button
          onClick={() =>
            onSubmitTeacherAnswer
              ? onSubmitTeacherAnswer()
              : handleSubmitResponse(
                  fieldResponse?.get('submitted_drawing_json'),
                  fieldResponse?.get('submitted_drawing_svg')
                )
          }
          disabled={hasEmptyResponse}
          data-test={`submit-${EXAM_QUESTION_TYPES.ILLUSTRATION}`}
        >
          {onSubmitTeacherAnswer ? 'View answer' : 'Save & continue'}
        </Button>
      </AssetUploadContext.Provider>
    );
  }

  return (
    <>
      <IllustrationAnswer question={question} response={response} />

      {!isQuestionReview && (
        <>
          <QuestionSolution
            headingText="Solution"
            questionId={question.get('id')}
            explanation={response.getIn(['solution', 'explanation'])}
            videoHash={response.getIn([
              'solution',
              'video',
              'wistia_hashed_id',
            ])}
            videoThumbnailUrl={WistiaUtils.getThumbnail(
              response.getIn(['solution', 'video'])
            )}
          />
          <WaffleFlag name={'questions-self-marking'}>
            <SelfMarking
              onChange={onSubmitMark}
              mark={response.get('text_marks_from_student')}
              totalMarks={Number(question.get('marks'))}
            />
          </WaffleFlag>
          {!isPrint && (
            <GrokSelfReflection
              value={response.get('grokked')}
              onChange={onSubmitGrok}
              isSaving={grokkingItemIsSaving}
              className={style.selfReflection}
            />
          )}
        </>
      )}
    </>
  );
};

IllustrationQuestion.propTypes = {
  ...questionTypePropInterface,
};
